/**
 * Relevant auth0 docs: https://auth0.com/docs/api-auth/tutorials/authorization-code-grant-pkce
 */
import { createHash, randomBytes } from 'crypto';
import request from 'request';
import { AUTH0_AUDIENCE, AUTH0_CLIENT_ID, AUTH0_DOMAIN } from './environment';
import { Store } from './store';
import express from 'express';
import * as https from 'https';
import { Server } from 'http';

let authServer: Server;
let pckeData: PKCEData;

export interface ChallengePair {
  verifier: string;
  challenge: string;
}

export interface AccessTokenResponse {
  response: request.Response;
  body: any;
}

export interface PKCEData {
  authorizeURL: string;
  challengePair: ChallengePair;
}

const AUTH0_CONFIG = {
  clientId: AUTH0_CLIENT_ID,
  authorizeEndpoint: `https://${AUTH0_DOMAIN}/authorize`,
  audience: AUTH0_AUDIENCE,
  scope: 'openid email profile',
  redirectUri: `http://localhost:PORT`,
  tokenEndpoint: `https://${AUTH0_DOMAIN}/oauth/token`
};

/**
 * Generate a new challenge pair and auth0 /authorize request URL
 */
export function generatePKCEData(authPort: number): PKCEData {
  const challengePair = generatePKCEChallengePair();
  return {
    authorizeURL: generateAuth0AuthorizeURL(challengePair, authPort),
    challengePair
  };
}

/**
 * After successfuly authenticating and redirecting back to the app, we
 * extract the `code` from the redirect URI and use that together with
 * the code verifier to request an access code
 */
export function requestAccessToken(
  authCode: string,
  pkceData: PKCEData,
  authPort: number
): Promise<AccessTokenResponse> {
  return new Promise((resolve, reject) => {
    /**
     * Make a POST request to Auth0 to get an access_token
     */
    const verifier = pkceData.challengePair.verifier;
    const options = generateAuth0TokenRequestOptions(
      authCode,
      verifier,
      authPort
    );
    request(options, (err, response, body) => {
      if (err) {
        return reject(err);
      }
      if (response.statusCode >= 200 && response.statusCode < 300) {
        return resolve({
          response: response,
          body: body
        });
      }
      /**
       * Non-2xx response, reject
       */
      return reject(body);
    });
  });
}

let store: Store;

export function setStore(s: Store) {
  store = s;
}

const ACCESS_TOKEN = 'access_token';
const ID_TOKEN = 'id_token';
const EXPIRES_AT = 'expires_at';

export function setSession(authResult: any) {
  const expiresAt = authResult.expires_in * 1000 + Date.now();
  store.set(ACCESS_TOKEN, authResult.access_token);
  store.set(ID_TOKEN, authResult.id_token);
  store.set(EXPIRES_AT, expiresAt);
}

export function clearSession() {
  store.delete(ACCESS_TOKEN);
  store.delete(ID_TOKEN);
  store.delete(EXPIRES_AT);
  https.get(`https://${AUTH0_DOMAIN}/v2/logout`);
}

export function isAuthenticated(): boolean {
  const expiresAt = store.get(EXPIRES_AT) || 0;
  return Date.now() < expiresAt;
}

export function getAccessTokenFromStore(): string | null {
  return store.get(ACCESS_TOKEN) || null;
}

export function getIdTokenFromStore(): string | null {
  return store.get(ID_TOKEN) || null;
}

export async function getAuthenticationUrl() {
  if (!authServer) {
    const p: number = await require('get-port')({ port: [62235, 47185] });

    const app = express();
    app.get('/', async req => {
      try {
        const authResult = await requestAccessToken(
          req.query.code,
          pckeData,
          p
        );
        setSession(authResult.body);
      } catch (err) {
        console.error(err);
        return clearSession();
      }
    });
    authServer = app.listen(p);
  }

  pckeData = generatePKCEData(authServer.address().port);
  return pckeData.authorizeURL;
}

// ==================================================================================== UTILS

/**
 * Base64 encode a buffer for use in a URL
 */
function base64URLEncode(buffer: Buffer): string {
  return buffer
    .toString('base64')
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

function generateSHA256(buffer: string): Buffer {
  return createHash('sha256')
    .update(buffer)
    .digest();
}

function generatePKCEChallengePair(): ChallengePair {
  const verifier = base64URLEncode(randomBytes(32));
  const challenge = base64URLEncode(generateSHA256(verifier));
  return { verifier, challenge };
}

function generateAuth0AuthorizeURL(
  challengePair: ChallengePair,
  authPort: number
): string {
  return (
    `${AUTH0_CONFIG.authorizeEndpoint}?audience=${AUTH0_CONFIG.audience}` +
    `&scope=${AUTH0_CONFIG.scope}` +
    `&response_type=code` +
    `&client_id=${AUTH0_CONFIG.clientId}` +
    `&code_challenge=${challengePair.challenge}` +
    `&code_challenge_method=S256` +
    `&redirect_uri=${AUTH0_CONFIG.redirectUri.replace(':PORT', `:${authPort}`)}`
  );
}

function generateAuth0TokenRequestOptions(
  authCode: string,
  verifier: string,
  authPort: number
): request.Options {
  return {
    method: 'POST',
    url: AUTH0_CONFIG.tokenEndpoint,
    headers: { 'content-type': 'application/json' },
    json: true,
    body: {
      grant_type: 'authorization_code',
      client_id: AUTH0_CONFIG.clientId,
      code_verifier: verifier,
      code: authCode,
      redirect_uri: AUTH0_CONFIG.redirectUri.replace(':PORT', `:${authPort}`),
      scope: AUTH0_CONFIG.scope
    }
  };
}
